import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { LogOut, Edit2, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import '../styles/AdminDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY;

const SalesReps = () => {
  const navigate = useNavigate();
  const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Check authentication
    const isAuth = sessionStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }

    fetchSalesReps();
  }, [navigate]);

  const fetchSalesReps = async () => {
    try {
      const response = await axios.get(`${API}/sales-reps`, {
        headers: {
          'X-API-Key': ADMIN_API_KEY
        }
      });
      setSalesReps(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching sales reps:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    navigate('/admin');
  };

  const startEdit = (rep) => {
    setEditingId(rep.id);
    setEditForm({
      name: rep.name,
      country: rep.country,
      region: rep.region || '',
      intent: rep.intent || '',
      booking_url: rep.booking_url
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (repId) => {
    setIsSaving(true);
    try {
      const updates = {
        name: editForm.name,
        country: editForm.country,
        region: editForm.region || null,
        intent: editForm.intent || null,
        booking_url: editForm.booking_url
      };

      await axios.put(`${API}/sales-reps/${repId}`, updates, {
        headers: {
          'X-API-Key': ADMIN_API_KEY,
          'Content-Type': 'application/json'
        }
      });

      // Refresh the list
      await fetchSalesReps();
      setEditingId(null);
      setEditForm({});
    } catch (error) {
      console.error('Error updating sales rep:', error);
      alert('Failed to update sales rep. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading sales representatives...</div>;
  }

  return (
    <div className="admin-dashboard">
      <Helmet>
        <title>PourMyBev - Sales Reps</title>
        <meta name="description" content="PourMyBev" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h2>Admin Dashboard</h2>
        </div>
        <nav className="sidebar-nav">
          <a href="/admin/dashboard" className="nav-item">Dashboard</a>
          <a href="/admin/sales-reps" className="nav-item active">Sales Reps</a>
        </nav>
        <button onClick={handleLogout} className="logout-btn">
          <LogOut size={18} />
          Logout
        </button>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1>Sales Representatives</h1>
            <p className="subtitle">Manage sales rep information and booking URLs</p>
          </div>
        </div>

        {/* Sales Reps Table */}
        <div className="table-section">
          <div className="table-container">
            <table className="data-table sales-reps-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Country</th>
                  <th>Region</th>
                  <th>Intent</th>
                  <th>Booking URL</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {salesReps.map((rep) => (
                  <tr key={rep.id}>
                    {editingId === rep.id ? (
                      <>
                        <td>
                          <Input
                            value={editForm.name}
                            onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                            className="edit-input"
                          />
                        </td>
                        <td>
                          <Input
                            value={editForm.country}
                            onChange={(e) => setEditForm({...editForm, country: e.target.value})}
                            className="edit-input"
                          />
                        </td>
                        <td>
                          <Select
                            value={editForm.region}
                            onValueChange={(value) => setEditForm({...editForm, region: value})}
                          >
                            <SelectTrigger className="edit-select">
                              <SelectValue placeholder="None" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="EMEA">EMEA</SelectItem>
                              <SelectItem value="APAC">APAC</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td>
                          <Select
                            value={editForm.intent}
                            onValueChange={(value) => setEditForm({...editForm, intent: value})}
                          >
                            <SelectTrigger className="edit-select">
                              <SelectValue placeholder="None" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td>
                          <Input
                            value={editForm.booking_url}
                            onChange={(e) => setEditForm({...editForm, booking_url: e.target.value})}
                            className="edit-input"
                          />
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              onClick={() => saveEdit(rep.id)}
                              className="save-btn"
                              disabled={isSaving}
                              title="Save changes"
                            >
                              <Save size={16} />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="cancel-btn"
                              disabled={isSaving}
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{rep.name}</td>
                        <td>{rep.country}</td>
                        <td>{rep.region || '-'}</td>
                        <td>{rep.intent || '-'}</td>
                        <td className="url-cell">
                          <a href={rep.booking_url} target="_blank" rel="noopener noreferrer">
                            {rep.booking_url}
                          </a>
                        </td>
                        <td>
                          <button
                            onClick={() => startEdit(rep)}
                            className="edit-btn"
                            title="Edit sales rep"
                          >
                            <Edit2 size={16} />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesReps;
